import React, { memo } from 'react';
import { Heading } from '@uk-source-web/heading';
import { Paragraph } from '@uk-source-web/paragraph';
import styled from 'styled-components';
import {
  DiscountHeader,
  DiscountInformationWrapper,
  HorizontailLine,
  RecurringDiscountHeader,
} from '../BasketDiscount.styles';
import { Spacer } from '@/components/Global/RegisterAccount/RegisterAccount.styles';
import { useAppSelector } from '@/store';
import handleTranslation from '@/helpers/handleTranslation';
import { t } from 'i18next';
import { translatedMDFValues } from '../../translatedValues';

const ParagraphWrapper = styled.span`
  && {
    display: inline-block ;
    width: auto ;
    max-width: fit-content;
    margin: 0 ;
  }
  
  && p {
    display: inline-block ;
    width: auto ;
    max-width: fit-content; 
    margin: 0 ;
  }
`;

interface BasketMoreInformationContentProps {
  basketSummary: { products: any[]; subtotal: number; dueNow: number; recurringCharges: number; estimatedVAT: number };
}

const BasketMoreInformationContent: React.FC<BasketMoreInformationContentProps> = memo(({ basketSummary }) => {
  const { userBasket } = useAppSelector(({ userBasket: { data } }) => ({
    userBasket: data,
  }));

  const { discountData } = useAppSelector(state => state.userDiscount);
  const vatLabel = handleTranslation('Estimated VAT', 'IVA');
  const discountLabel = handleTranslation('Discounts', 'Descontos');

  const monthlyGross = parseFloat(userBasket?.total?.monthly?.gross) || 0;
  const upfrontGross = parseFloat(userBasket?.total?.upfront?.gross) || 0;
  const totalGross = monthlyGross + upfrontGross;
  const dueMonthlyLabel = t('dueMonthlyNoTax');
  const dueNowDescription = t('dueNowDescription');
  const recurringDiscountsText = handleTranslation('Recurring discounts', 'Descontos recorrentes');
  const billingCyclesText = handleTranslation('billing cycles', 'ciclos de faturação');

  if (discountData) {
    basketSummary = {
        ...basketSummary,
        recurringCharges: discountData.recurringCharges,
        dueNow: discountData.dueNow,
        subtotal: discountData.subtotal,
        estimatedVAT: discountData.estimatedVAT
    };
}

const Subtotal = translatedMDFValues.Subtotal;
const EstimatedVAT = translatedMDFValues.EstimatedVAT;
const DueNow = translatedMDFValues.DueNow;
const DueNowNoe = translatedMDFValues.DueNowNote;
const EstimatedRecurringCharges = translatedMDFValues.EstimatedRecurringCharges;
const DuemMonthlyExcludingTax = translatedMDFValues.DuemMonthlyExcludingTax;
  return (
    <>
      <DiscountInformationWrapper>
        <ParagraphWrapper>
          <Paragraph size={1} weight={2}>
            {Subtotal}
          </Paragraph>
        </ParagraphWrapper>
        <ParagraphWrapper>
          <Paragraph size={2} weight={2}>
          &euro; { basketSummary?.subtotal ?? totalGross}
        </Paragraph>
        </ParagraphWrapper>
      </DiscountInformationWrapper>
      {discountData && discountData?.upfrontDiscount !== 0 && (
        <DiscountInformationWrapper>
          <ParagraphWrapper>
            <Paragraph size={1} weight={2}>
              {discountLabel}
            </Paragraph>
          </ParagraphWrapper>
          <ParagraphWrapper>
            <Paragraph size={2} weight={2}>
              -&euro; {(discountData?.upfrontDiscount || 0).toFixed(2)}
            </Paragraph>
          </ParagraphWrapper>
        </DiscountInformationWrapper>
      )}
      <DiscountInformationWrapper>
        <ParagraphWrapper>
          <Paragraph size={1} weight={2}>
            {EstimatedVAT}
          </Paragraph>
        </ParagraphWrapper>
        <ParagraphWrapper>
          <Paragraph size={2} weight={2}>
            &euro; {basketSummary?.estimatedVAT}
          </Paragraph>
        </ParagraphWrapper>
      </DiscountInformationWrapper>

      <>
        <DiscountInformationWrapper>
          <DiscountHeader>
            <Heading size={1} level={3} weight={3} text={DueNow} />
            <span>{DueNowNoe}</span>
          </DiscountHeader>
          <ParagraphWrapper>
            <Paragraph size={2} weight={3}>
              &euro; {basketSummary?.dueNow}
            </Paragraph>
          </ParagraphWrapper>
        </DiscountInformationWrapper>
        <Spacer h={15} />
      </>

      <HorizontailLine />
      <Spacer h={12} />
      {monthlyGross > 0 && upfrontGross > 0 && (
        <>
          <HorizontailLine />
          <Spacer h={20} />
        </>
      )}
      {monthlyGross > 0 && (
        <>
          <DiscountInformationWrapper>
            <DiscountHeader>
              <Heading size={1} level={3} weight={3} text={EstimatedRecurringCharges} />
              <ParagraphWrapper>
                <Paragraph size={1} weight={1}>
                  {DuemMonthlyExcludingTax}
                </Paragraph>
              </ParagraphWrapper>
            </DiscountHeader>
            <ParagraphWrapper>
              <Paragraph size={2} weight={3}>
                &euro; {discountData ? discountData?.recurringCharges : basketSummary?.recurringCharges}
              </Paragraph>
            </ParagraphWrapper>
          </DiscountInformationWrapper>
          <Spacer h={20} />
          {discountData && (
            <DiscountInformationWrapper>
              <RecurringDiscountHeader>
                <Heading size={1} level={3} text={recurringDiscountsText} />
                {discountData?.billingCycles &&
                  discountData?.billingCycles !== 'UNLIMITED' &&
                  discountData?.billingCycles !== '0' && (
                    <ParagraphWrapper>
                      <Paragraph size={1} weight={1}>
                        {discountData?.billingCycles} {billingCyclesText}
                      </Paragraph>
                    </ParagraphWrapper>
                  )}
              </RecurringDiscountHeader>
              <ParagraphWrapper>
                <Paragraph size={1} weight={2}>
                  -&euro; {(discountData ? discountData?.recurringDiscount : 0.0).toFixed(2)}
                </Paragraph>
              </ParagraphWrapper>
            </DiscountInformationWrapper>
          )}
        </>
      )}
    </>
  );
});

export default BasketMoreInformationContent;
